import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import { HatLoading } from "../../components/loading-screen";
import { useFullCurrentUser } from "../../hooks/useCurrentUser";
import { useMyProducts } from "../../hooks/useMyProducts";
import { useProductFeatures } from "../../hooks/useProductFeatures";
import { useSchoolLevels } from "../../hooks/useSchoolLevels";

const ProtectedRoute = observer(
  ({
    children,
    requiredRole = [],
    requiredLevel = [],
    requireWrite = false,
  }) => {
    const { activeUserRecord, userRecordsLoading } = useFullCurrentUser();
    const { matchesSchoolLevels, isLoadingSchoolLevels, schoolLevels } =
      useSchoolLevels();
    const { activeProduct } = useProductFeatures();
    // Same ["myProducts"] query useProductFeatures reads — served from cache.
    const { products } = useMyProducts();

    const location = useLocation();

    if (userRecordsLoading || isLoadingSchoolLevels) {
      return <HatLoading />;
    }

    if (!activeUserRecord || !activeUserRecord.userID) {
      return <Navigate to="/" state={{ from: location }} replace />;
    }

    // The district-less roles — superadmin (parented to a union) and Hats staff
    // (parented to an org) — act as district admins once they have entered a
    // district. Mirrors hasEnteredADistrict in useCurrentUser; the two must
    // agree or a route admits someone the page then cannot scope.
    const effectiveRole =
      (activeUserRecord.role === "superadmin" ||
        activeUserRecord.role === "hatsstaff") &&
      activeUserRecord.activeDistrictId &&
      !activeUserRecord.isReadOnlyStaff
        ? "districtadmin"
        : activeUserRecord.role;

    // Check both actual and effective role, so these roles keep reaching their
    // own routes (/superadmin/dashboard, /hatsstaff/dashboard) after entering.
    if (
      requiredRole.length > 0 &&
      !requiredRole.includes(effectiveRole) &&
      !requiredRole.includes(activeUserRecord.role)
    ) {
      console.log(
        "🔒 Does not have required role: ",
        requiredRole,
        effectiveRole,
      );
      return <Navigate to="/404" replace />;
    }

    // Pages that only exist to change data
    if (requireWrite && activeUserRecord.isReadOnlyStaff) {
      return <Navigate to="/404" replace />;
    }

    // A climber holding products with none active never passed through
    // /post-login (deep link, bookmark, cleared storage). Their plan codes are
    // empty, so useSchoolLevels falls through to its "wf" default and would
    // 404 them off their own routes — send them to the selector instead. Must
    // precede the level check for that reason. Educators are exempt: they get
    // the union of educatorPlanCodes and need no active product.
    //
    // This also catches a climber whose only program has ended or not yet
    // started: useProductFeatures refuses to resolve a product outside its
    // schedule, so the sole-product fallback no longer hands one back and this
    // is no longer multi-product only. An errored products query leaves
    // `products` empty, so the guard stays shut rather than locking anyone out.
    if (
      activeUserRecord.role === "climber" &&
      products.length > 0 &&
      !activeProduct
    ) {
      // Carry the attempted route through: post-login reads a `state` query
      // param and routes there in preference to the dashboard, which is how
      // the login page hands off. Without it a bookmarked deep link is
      // silently swapped for the dashboard once they pick a product.
      return (
        <Navigate
          to={`/post-login?state=${encodeURIComponent(
            location.pathname + location.search,
          )}`}
          replace
        />
      );
    }

    if (requiredLevel.length > 0 && !matchesSchoolLevels(requiredLevel)) {
      console.log(
        "🔒 Does not have required school level: ",
        requiredLevel,
        schoolLevels,
      );
      return <Navigate to="/404" replace />;
    }

    return children;
  },
);

export default ProtectedRoute;
